import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Card } from '../components/ui';
import { CloseDay, closedKey } from '../components/CloseDay';
import { formatEuro } from '../components/forms';
import { formatPhone } from '../components/calendar/AppointmentPanel';
import { TitleBlock } from '../components/forms';
import { AppointmentTags } from '../components/AppointmentTags';
import { api, type Me, type Salon } from '../lib/api';
import { useAppointment, useCalendar, useCatalog, useLiveCalendar, type CalendarAppointment } from '../lib/calendar';
import { useLocale, useT, type TKey } from '../lib/i18n';
import { addDays, formatDateLong, formatTime, SALON_TZ, todayIn, zonedHour } from '../lib/time';
import { useFirstLocation } from './setup-hours';
import { ShareLinkButton } from './booking-settings';

function useNow() {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);
  return now;
}

/** Vandaag: the day at a glance (plan 2.4). */
export function TodayPage() {
  const { orgId } = useParams({ from: '/s/$orgId' });
  const t = useT();
  const locale = useLocale();
  const now = useNow();
  useLiveCalendar(orgId);
  const tz = SALON_TZ;
  const today = todayIn(tz);
  const me = useQuery({ queryKey: ['me'], queryFn: () => api<Me>('/me') });
  const salon = useQuery({ queryKey: ['salon', orgId], queryFn: () => api<Salon>(`/orgs/${orgId}`) });
  const location = useFirstLocation(orgId);
  const cal = useCalendar(orgId, location.data?.id, today, today);
  const catalog = useCatalog(orgId);

  const firstName = me.data?.user.name.split(' ')[0] ?? '';
  const hour = zonedHour(now, tz);
  const greeting = hour < 12 ? t('today.morning') : hour < 18 ? t('today.afternoon') : t('today.evening');
  const isStaff = salon.data?.role === 'staff';
  const own = salon.data?.staffMemberId;

  const appointments = (cal.data?.appointments ?? []).filter((a) => a.status !== 'cancelled' && (!isStaff || !own || a.staffIds.includes(own)));
  const staffName = (id: string) => cal.data?.staff.find((s) => s.id === id);
  const nextIndex = appointments.findIndex((a) => Date.parse(a.endsAt) > now);
  const working = (cal.data?.staff ?? []).filter((s) => s.scheduled.length > 0 || appointments.some((a) => a.staffIds.includes(s.id)));

  const manager = !!salon.data && !isStaff;
  const since = useLastVisit(orgId);
  const overview = useQuery({
    queryKey: ['today', orgId, location.data?.id, since],
    queryFn: () => api<Overview>(`/orgs/${orgId}/today?locationId=${location.data!.id}&since=${encodeURIComponent(since)}`),
    enabled: manager && !!location.data,
    refetchInterval: 120_000,
  });

  const status = useQuery({ queryKey: ['setup-status', orgId], queryFn: () => api<{ services: number; team: number; onlineBookings: number; teamInvited: boolean }>(`/orgs/${orgId}/setup-status`) });
  const wizardDone = (salon.data?.salon.onboardingStep ?? 0) >= 5;
  const st = status.data;
  type To = '/s/$orgId/welkom' | '/s/$orgId/salon/openingstijden' | '/s/$orgId/salon/diensten' | '/s/$orgId/salon/team' | '/s/$orgId/salon/online-boeken';
  const steps: Array<{ key: TKey; to: To; done: boolean; search?: Record<string, string> }> = [
    { key: 'today.setup.wizard', to: '/s/$orgId/welkom', done: wizardDone },
    { key: 'today.setup.services', to: '/s/$orgId/salon/diensten', done: (st?.services ?? catalog.data?.reduce((n, c) => n + c.services.length, 0) ?? 0) > 0 },
    { key: 'today.setup.testBooking', to: '/s/$orgId/salon/online-boeken', done: (st?.onlineBookings ?? 0) > 0 },
    { key: 'today.setup.booking', to: '/s/$orgId/salon/online-boeken', done: sharedBefore(orgId) },
    { key: 'today.setup.team', to: '/s/$orgId/salon/team', done: !!st && (st.teamInvited || (wizardDone && st.team === 1)) },
  ];
  // The checklist is for whoever sets up the salon; staff can't change these settings.
  const setupDone = isStaff || steps.every((s) => s.done);

  const upcoming = appointments.filter((a) => Date.parse(a.endsAt) > now);
  const next = upcoming.find((a) => Date.parse(a.startsAt) > now) ?? upcoming[0];
  // Once today is done, the card shows who comes first tomorrow.
  const tomorrowCal = useCalendar(orgId, cal.data && !next ? location.data?.id : undefined, addDays(today, 1), addDays(today, 1));
  const firstTomorrow = (tomorrowCal.data?.appointments ?? []).find((a) => a.status !== 'cancelled' && (!isStaff || !own || a.staffIds.includes(own)));
  const spotlight = next ?? firstTomorrow;
  const nextWho = next ? staffName(next.staffIds[0]!)?.displayName : undefined;
  const summary =
    appointments.length === 0
      ? t('today.hero.empty')
      : next
        ? `${upcoming.length === 1 ? t('today.sum.one') : t('today.sum.many', { n: upcoming.length })} ${t('today.hero.next', { time: formatTime(next.startsAt, tz), name: next.customerName ?? t('cal.noClient'), who: nextWho ?? '' })}`
        : t('today.hero.done');

  return (
    <div className="flex w-full flex-col gap-8">
      <TitleBlock
        above={<p className="eyebrow first-letter:uppercase">{formatDateLong(today, locale)}</p>}
        title={`${greeting}${firstName ? `, ${firstName}` : ''}.`}
        intro={summary}
        action={
          <>
            <Link
              to="/s/$orgId/agenda"
              params={{ orgId }}
              search={{ nieuw: 1 }}
              data-tour="new"
              className="hidden min-h-11 items-center rounded-full bg-accent px-5 text-[15px] font-medium text-on-accent md:inline-flex shadow-[inset_0_1px_0_rgb(255_255_255/0.35),0_1px_2px_rgb(2_53_42/0.14)] hover:bg-accent-strong"
            >
              + {t('cal.newAppointment')}
            </Link>
            {salon.data && (
              <span data-tour="share" className="inline-flex">
                <ShareLinkButton orgId={orgId} slug={salon.data.salon.slug} />
              </span>
            )}
          </>
        }
      />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_380px] 2xl:grid-cols-[minmax(0,1fr)_440px]">
        <div className="flex min-w-0 flex-col gap-6">
          {appointments.length > 0 && <DayNumbers appointments={appointments} upcoming={upcoming.length} overview={manager ? overview.data : undefined} />}
          {spotlight && (
            <NextClient
              orgId={orgId}
              a={spotlight}
              tz={tz}
              label={spotlight === firstTomorrow ? t('today.tomorrow') : Date.parse(spotlight.startsAt) <= now ? t('today.now.label') : t('today.next')}
              person={(tomorrowCal.data && spotlight === firstTomorrow ? tomorrowCal.data.staff : (cal.data?.staff ?? [])).find((s) => s.id === spotlight.staffIds[0])}
              showPrice={manager}
            />
          )}
          <Card className="flex flex-col gap-1 p-0">
            <div className="flex items-baseline justify-between gap-3 px-5 pb-2 pt-5 md:px-6">
              <h2 className="display text-[22px] md:text-[26px]">{t('today.inAgenda')}</h2>
              {appointments.length > 0 && <span className="text-sm text-muted">{appointments.length === 1 ? t('today.oneAppointment') : t('today.appointments', { n: appointments.length })}</span>}
            </div>
            {cal.isPending ? (
              <p className="px-6 pb-6 text-muted">{t('common.loading')}</p>
            ) : appointments.length === 0 ? (
              <div className="flex flex-col items-start gap-2 px-6 pb-8 pt-2">
                <p className="font-medium">{t('today.empty.title')}</p>
                <p className="max-w-prose text-sm text-muted">{t('today.empty.body')}</p>
              </div>
            ) : (
              <>
                {/* Column heads for the wide list; phones get the compact rows. */}
                <div aria-hidden className={`hidden items-center gap-3 border-b border-line px-5 pb-2 md:grid md:px-6 ${ROW_COLS} [&>span]:eyebrow`}>
                  <span>{t('today.col.time')}</span>
                  <span className="pl-[15px]">{t('today.col.client')}</span>
                  <span className="hidden 2xl:block">{t('today.col.service')}</span>
                  <span>{t('today.col.who')}</span>
                  <span className={manager ? 'text-right' : ''}>{manager ? t('today.col.price') : ''}</span>
                </div>
                <ul className="flex flex-col px-2 pb-3 pt-1 md:px-3">
                  {appointments.map((a, i) => (
                    <li key={a.id}>
                      {i === nextIndex && Date.parse(a.startsAt) > now && i > 0 && <NowLine time={formatTime(new Date(now).toISOString(), tz)} />}
                      <AppointmentRow orgId={orgId} a={a} tz={tz} past={Date.parse(a.endsAt) <= now} current={Date.parse(a.startsAt) <= now && Date.parse(a.endsAt) > now} person={staffName(a.staffIds[0]!)} showPrice={manager} />
                    </li>
                  ))}
                </ul>
              </>
            )}
          </Card>
        </div>

        <div className="flex flex-col gap-4">
          {appointments.length > 0 && upcoming.length === 0 && location.data && <CloseDayCard orgId={orgId} locationId={location.data.id} date={today} />}
          {manager && overview.data && <TodoCard orgId={orgId} items={overview.data.undelivered} />}
          {!setupDone && <SetupCard orgId={orgId} steps={steps} />}
          <TeamToday staff={working} appointments={appointments} tz={tz} now={now} />
          {manager && overview.data && <ActivityCard orgId={orgId} items={overview.data.activity} tz={tz} />}
        </div>
      </div>
    </div>
  );
}

type Overview = {
  occupancy: { today: number | null; week: number | null };
  clients: { new: number; returning: number };
  activity: Array<{ type: 'created' | 'cancelled' | 'moved'; at: string; appointmentId: string; startsAt: string; clientName: string | null }>;
  undelivered: Array<{ id: string; kind: string; toAddress: string; error: string | null; at: string; appointmentId: string | null; customerId: string | null; clientName: string | null }>;
};

/**
 * "Since your last visit" on this device: the time this page was last opened (at most a week back).
 * Read once when the page opens, then moved to now.
 */
function useLastVisit(orgId: string) {
  const [since] = useState(() => {
    const fallback = new Date(Date.now() - 86_400_000).toISOString();
    try {
      const prev = localStorage.getItem(`vandaag-seen:${orgId}`);
      localStorage.setItem(`vandaag-seen:${orgId}`, new Date().toISOString());
      return prev ?? fallback;
    } catch {
      return fallback;
    }
  });
  return since;
}

const euro = (cents: number) => formatEuro(cents).replace(/,00$/, '');

/** The day in numbers, set like the totals at the foot of a page in the appointment book. */
function DayNumbers({ appointments, upcoming, overview }: { appointments: CalendarAppointment[]; upcoming: number; overview?: Overview }) {
  const t = useT();
  const pct = (v: number | null) => (v === null ? '—' : `${Math.round(v * 100)}%`);
  const cells: Array<{ label: string; value: string; sub: string; bar?: number | null }> = [
    { label: t('today.stat.appointments'), value: String(appointments.length), sub: upcoming > 0 ? t('today.stat.toGo', { n: upcoming }) : t('today.stat.allDone') },
  ];
  if (overview) {
    cells.push(
      { label: t('today.stat.occupancy'), value: pct(overview.occupancy.today), sub: t('today.stat.week', { pct: pct(overview.occupancy.week) }), bar: overview.occupancy.today },
      { label: t('today.stat.revenue'), value: euro(appointments.filter((a) => a.status !== 'no_show').reduce((n, a) => n + a.priceCents, 0)), sub: t('today.stat.revenueHint') },
      { label: t('today.stat.clients'), value: String(overview.clients.returning), sub: t('today.stat.new', { n: overview.clients.new }) },
    );
  }
  return (
    <div className={`grid gap-px overflow-hidden rounded-[var(--radius-card)] border border-line bg-line shadow-[0_1px_2px_rgb(2_53_42/0.04)] ${cells.length > 1 ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-1'}`}>
      {cells.map((c) => (
        <div key={c.label} className="flex flex-col bg-surface px-4 py-3 md:px-5 md:py-4">
          <span className="eyebrow">{c.label}</span>
          <span className="display mt-1.5 text-[30px] leading-none tabular-nums md:mt-2 md:text-[40px]">{c.value}</span>
          <span className="mt-1.5 text-[13px] text-muted">{c.sub}</span>
          {c.bar !== undefined && (
            <span aria-hidden className="mt-2.5 h-1 overflow-hidden rounded-full bg-subtle">
              <span className="block h-full rounded-full bg-ink" style={{ width: `${Math.round((c.bar ?? 0) * 100)}%` }} />
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

/** The next client as an appointment card: when, who, what, and what to know before they sit down. */
function NextClient({ orgId, a, tz, label, person, showPrice }: { orgId: string; a: CalendarAppointment; tz: string; label: string; person?: { displayName: string; color: string }; showPrice: boolean }) {
  const t = useT();
  const detail = useAppointment(orgId, a.id);
  const c = detail.data?.customer;
  const name = a.customerName ?? (a.source === 'walk_in' ? t('cal.walkIn') : t('cal.noClient'));
  const button = 'inline-flex min-h-10 items-center rounded-full border border-line bg-surface px-4 text-sm font-medium hover:bg-subtle';
  return (
    <section className="flex flex-col overflow-hidden rounded-[var(--radius-card)] border border-line bg-surface shadow-[0_1px_2px_rgb(2_53_42/0.04)] sm:flex-row">
      {/* The date block of the appointment card, in ink. */}
      <div className="relative flex shrink-0 flex-col justify-center gap-1 overflow-hidden bg-frame px-6 py-5 text-canvas sm:w-56">
        <div aria-hidden className="pointer-events-none absolute -bottom-16 -left-10 size-44 rounded-full bg-cream/15 blur-3xl" />
        <span className="eyebrow relative text-canvas/60">{label}</span>
        <span className="display relative text-[44px] leading-none tabular-nums sm:text-[56px]">{formatTime(a.startsAt, tz)}</span>
        <span className="relative text-[14px] text-canvas/70">{t('today.until', { time: formatTime(a.endsAt, tz) })}</span>
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-3 p-5 md:px-6">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <h2 className="display truncate text-[32px] leading-tight">{name}</h2>
          <AppointmentTags a={a} />
        </div>
        <p className="flex flex-wrap items-center gap-x-2 text-[15px]">
          <span className="font-medium">{a.services.join(' + ')}</span>
          {person && (
            <span className="flex items-center gap-1.5 text-muted">
              · <span className="size-2.5 rounded-full" style={{ backgroundColor: person.color }} />
              {t('today.with', { name: person.displayName })}
            </span>
          )}
          {showPrice && a.priceCents > 0 && <span className="text-muted">· {euro(a.priceCents)}</span>}
        </p>
        {c?.alertNote && <p className="rounded-[var(--radius-control)] bg-warning-soft px-3 py-2 text-sm text-warning">⚠ {c.alertNote}</p>}
        {a.note && <p className="text-sm italic text-muted">{a.note}</p>}
        <div className="mt-auto flex flex-wrap items-center gap-2 pt-1">
          {c && (
            <span className="mr-2 text-[13px] text-muted">
              {c.visits === 0 ? t('today.firstVisit') : t('appt.visits', { n: c.visits })}
              {c.noShows > 0 && <span className="text-danger"> · {t('appt.noShows', { n: c.noShows })}</span>}
            </span>
          )}
          {c?.phone && (
            <>
              <a href={`tel:${c.phone}`} className={button}>
                {t('appt.call')} <span className="ml-1.5 tabular-nums text-muted">{formatPhone(c.phone)}</span>
              </a>
              <a href={`https://wa.me/${c.phone.replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className={button}>
                {t('appt.whatsapp')}
              </a>
            </>
          )}
          <Link to="/s/$orgId/agenda" params={{ orgId }} search={{ afspraak: a.id }} className="inline-flex min-h-10 items-center rounded-full bg-ink px-4 text-sm font-medium text-canvas hover:bg-frame-soft">
            {t('today.openInAgenda')}
          </Link>
        </div>
      </div>
    </section>
  );
}

function ActivityCard({ orgId, items, tz }: { orgId: string; items: Overview['activity']; tz: string }) {
  const t = useT();
  const locale = useLocale();
  if (!items.length) return null;
  const when = (iso: string) => new Intl.DateTimeFormat(locale === 'nl' ? 'nl-NL' : 'en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: tz }).format(new Date(iso));
  return (
    <Card>
      <h2 className="display text-[22px]">{t('today.since')}</h2>
      <ul className="mt-2 flex flex-col">
        {items.map((a) => (
          <li key={a.appointmentId + a.type + a.at}>
            <Link to="/s/$orgId/agenda" params={{ orgId }} search={{ afspraak: a.appointmentId }} className="-mx-2 flex flex-col rounded-[var(--radius-control)] px-2 py-2 hover:bg-subtle">
              <span className="text-sm">
                <span className={a.type === 'cancelled' ? 'font-semibold text-danger' : 'font-semibold'}>{t(`today.act.${a.type}` as TKey)}</span> · {a.clientName ?? t('today.someone')}
              </span>
              <span className="text-sm text-muted first-letter:uppercase">{when(a.startsAt)}</span>
            </Link>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** At the end of the day: one tap to "Dag afsluiten", or a quiet tick once it's done on this device. */
function CloseDayCard({ orgId, locationId, date }: { orgId: string; locationId: string; date: string }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [closed, setClosed] = useState(() => {
    try {
      return localStorage.getItem(closedKey(orgId, date)) === '1';
    } catch {
      return false;
    }
  });
  return (
    <>
      {closed ? (
        <button type="button" onClick={() => setOpen(true)} className="flex items-center justify-between rounded-[var(--radius-card)] border border-line bg-surface px-5 py-3 text-left text-[15px] hover:bg-subtle">
          <span className="font-medium">✓ {t('close.done')}</span>
          <span aria-hidden className="text-muted">›</span>
        </button>
      ) : (
        <section className="relative overflow-hidden rounded-[var(--radius-card)] bg-frame p-5 text-canvas shadow-[0_10px_30px_rgb(2_53_42/0.18)]">
          <div aria-hidden className="pointer-events-none absolute -right-12 -top-16 size-44 rounded-full bg-cream/15 blur-3xl" />
          <p className="eyebrow relative !text-canvas/60">{t('close.eyebrow')}</p>
          <p className="display relative mt-1 text-[28px] leading-tight">{t('close.title')}</p>
          <p className="relative mt-1 text-[14px] text-canvas/70">{t('close.intro')}</p>
          <button type="button" onClick={() => setOpen(true)} className="relative mt-4 inline-flex min-h-11 items-center rounded-full bg-cream px-5 text-[15px] font-semibold text-frame hover:bg-white">
            {t('close.start')}
          </button>
        </section>
      )}
      {open && (
        <CloseDay
          orgId={orgId}
          locationId={locationId}
          date={date}
          onClose={(done) => {
            setOpen(false);
            if (done) setClosed(true);
          }}
        />
      )}
    </>
  );
}

/** "Te doen": things that need a person, as tinted rows with their own action. */
function TodoCard({ orgId, items }: { orgId: string; items: Overview['undelivered'] }) {
  const t = useT();
  if (!items.length) return null;
  return (
    <Card className="flex flex-col gap-3 overflow-hidden p-0">
      <div className="flex items-baseline justify-between px-5 pt-5">
        <h2 className="display text-[22px]">{t('today.todo')}</h2>
        <span className="tag bg-warning-soft text-warning">{items.length}</span>
      </div>
      <ul className="flex flex-col">
        {items.map((m) => (
          <li key={m.id} className="flex items-start gap-3 border-t border-line bg-gradient-to-r from-warning-soft/70 to-transparent px-5 py-3">
            <span aria-hidden className="mt-1.5 size-2 shrink-0 rounded-full bg-warning" />
            <span className="min-w-0 flex-1">
              <span className="block text-[15px] font-medium">{t('today.undelivered', { name: m.clientName ?? t('today.someone') })}</span>
              <span className="block truncate text-[13px] text-muted">
                {m.toAddress}
                {m.error ? ` · ${m.error}` : ''}
              </span>
            </span>
            <Link
              to="/s/$orgId/klanten"
              params={{ orgId }}
              search={m.customerId ? { klant: m.customerId } : {}}
              className="shrink-0 rounded-full bg-frame px-3 py-1.5 text-[13px] font-medium text-canvas hover:bg-frame-soft"
            >
              {t('today.openClient')}
            </Link>
          </li>
        ))}
      </ul>
      <p className="px-5 pb-4 text-[13px] text-muted">{t('today.undeliveredHint')}</p>
    </Card>
  );
}

function NowLine({ time }: { time: string }) {
  return (
    <div className="flex items-center gap-2 px-2 py-1.5" aria-hidden>
      <span className="w-14 shrink-0 text-xs font-semibold tabular-nums text-accent-strong">{time}</span>
      <span className="size-2 rounded-full bg-accent" />
      <span className="h-px flex-1 bg-accent" />
    </div>
  );
}

// Time · client · service (wide screens) · who · price. Written out in full so Tailwind sees the classes.
const ROW_COLS = 'md:grid-cols-[64px_minmax(0,1fr)_120px_72px] 2xl:grid-cols-[100px_minmax(0,1.2fr)_minmax(0,1fr)_150px_72px]';

function AppointmentRow({ orgId, a, tz, past, current, person, showPrice }: { orgId: string; a: CalendarAppointment; tz: string; past: boolean; current: boolean; person?: { displayName: string; color: string }; showPrice: boolean }) {
  const t = useT();
  const name = a.customerName ?? (a.source === 'walk_in' ? t('cal.walkIn') : t('cal.noClient'));
  return (
    <Link
      to="/s/$orgId/agenda"
      params={{ orgId }}
      search={{ afspraak: a.id }}
      className={`group flex items-center gap-3 rounded-[14px] px-2 py-2.5 transition-colors hover:bg-subtle md:grid md:px-3 ${ROW_COLS} ${past ? 'opacity-50' : ''} ${current ? 'bg-accent-soft/70' : ''}`}
    >
      <span className="w-14 shrink-0 text-[15px] font-semibold tabular-nums md:w-auto">
        {formatTime(a.startsAt, tz)}
        <span className="hidden font-normal text-muted 2xl:inline">–{formatTime(a.endsAt, tz)}</span>
      </span>
      <span className="flex min-w-0 flex-1 items-center gap-3">
        <span aria-hidden className="h-9 w-[3px] shrink-0 rounded-full" style={{ backgroundColor: person?.color ?? 'var(--color-line)' }} />
        <span className="min-w-0 flex-1">
          <span className="flex min-w-0 items-center gap-2">
            <span className="truncate font-medium">{name}</span>
            <AppointmentTags a={a} className="shrink-0" />
          </span>
          <span className="block truncate text-sm text-muted 2xl:hidden">{a.services.join(' + ')}</span>
        </span>
      </span>
      <span className="hidden truncate text-[15px] text-muted 2xl:block">{a.services.join(' + ')}</span>
      <span className="hidden min-w-0 items-center gap-2 text-sm text-muted sm:flex">
        {person && <span className="size-2 shrink-0 rounded-full" style={{ backgroundColor: person.color }} />}
        <span className="truncate">{person?.displayName}</span>
      </span>
      <span className="hidden text-right text-sm tabular-nums text-muted md:block">{showPrice && a.priceCents > 0 ? euro(a.priceCents) : ''}</span>
    </Link>
  );
}

/** Who works today, each in their own colour, with their day drawn as a bar (booked parts filled). */
function TeamToday({ staff, appointments, tz, now }: { staff: Array<{ id: string; displayName: string; color: string; scheduled: Array<{ start: string; end: string }> }>; appointments: CalendarAppointment[]; tz: string; now: number }) {
  const t = useT();
  return (
    <Card className="flex flex-col gap-4">
      <h2 className="display text-[22px]">{t('today.working')}</h2>
      {staff.length === 0 && <p className="text-sm text-muted">{t('today.nobody')}</p>}
      <ul className="flex flex-col gap-4">
        {staff.map((s) => {
          const mine = appointments.filter((a) => a.staffIds.includes(s.id));
          const from = Math.min(...s.scheduled.map((r) => Date.parse(r.start)), ...mine.map((a) => Date.parse(a.startsAt)));
          const to = Math.max(...s.scheduled.map((r) => Date.parse(r.end)), ...mine.map((a) => Date.parse(a.endsAt)));
          const span = Math.max(1, to - from);
          const pos = (ms: number) => `${((ms - from) / span) * 100}%`;
          return (
            <li key={s.id} className="flex flex-col gap-2">
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-2.5 font-medium">
                  <span className="flex size-7 items-center justify-center rounded-full text-[12px] font-semibold text-white" style={{ backgroundColor: s.color }}>
                    {s.displayName.slice(0, 1).toUpperCase()}
                  </span>
                  {s.displayName}
                </span>
                <span className="text-right text-[13px] tabular-nums text-muted">
                  {s.scheduled.map((r) => `${formatTime(r.start, tz)}–${formatTime(r.end, tz)}`).join(', ') || t('cal.notWorking')}
                  {' · '}
                  {mine.length === 1 ? t('today.oneAppointment') : t('today.appointments', { n: mine.length })}
                </span>
              </div>
              {Number.isFinite(from) && (
                <div className="relative h-2 overflow-hidden rounded-full bg-subtle" aria-hidden>
                  {mine.map((a) => (
                    <span key={a.id} className="absolute inset-y-0 rounded-full" style={{ left: pos(Date.parse(a.startsAt)), width: `calc(${pos(Date.parse(a.endsAt))} - ${pos(Date.parse(a.startsAt))})`, backgroundColor: s.color, opacity: Date.parse(a.endsAt) <= now ? 0.4 : 0.9 }} />
                  ))}
                  {now > from && now < to && <span className="absolute inset-y-[-2px] w-0.5 bg-ink" style={{ left: pos(now) }} />}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

/** The setup checklist with its progress as a ring. */
function SetupCard({ orgId, steps }: { orgId: string; steps: Array<{ key: TKey; to: '/s/$orgId/welkom' | '/s/$orgId/salon/openingstijden' | '/s/$orgId/salon/diensten' | '/s/$orgId/salon/team' | '/s/$orgId/salon/online-boeken'; done: boolean }> }) {
  const t = useT();
  const done = steps.filter((s) => s.done).length;
  const r = 15;
  const c = 2 * Math.PI * r;
  return (
    <Card className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <svg width="40" height="40" viewBox="0 0 40 40" aria-hidden className="-rotate-90">
          <circle cx="20" cy="20" r={r} fill="none" stroke="var(--color-subtle)" strokeWidth="4" />
          <circle cx="20" cy="20" r={r} fill="none" stroke="var(--color-accent)" strokeWidth="4" strokeLinecap="round" strokeDasharray={`${(done / steps.length) * c} ${c}`} />
        </svg>
        <div>
          <h2 className="display text-[22px]">{t('today.setup.title')}</h2>
          <p className="text-[13px] text-muted">{t('today.setup.progress', { done, total: steps.length })}</p>
        </div>
      </div>
      <ol className="flex flex-col">
        {steps.map((s) => (
          <li key={s.key}>
            <Link to={s.to} params={{ orgId }} className="flex items-center gap-3 rounded-[12px] px-2 py-2 hover:bg-subtle">
              <span className={`flex size-5 shrink-0 items-center justify-center rounded-full border text-[11px] ${s.done ? 'border-ink bg-ink text-canvas' : 'border-line bg-surface'}`}>{s.done ? '✓' : ''}</span>
              <span className={`flex-1 text-[15px] ${s.done ? 'text-muted line-through decoration-line' : ''}`}>{t(s.key)}</span>
              <span aria-hidden className="text-muted">›</span>
            </Link>
          </li>
        ))}
      </ol>
    </Card>
  );
}

/** Whether this device copied or shared the booking link before (a per-device hint for the checklist). */
export function sharedBefore(orgId: string) {
  try {
    return localStorage.getItem(`shared-link:${orgId}`) === '1';
  } catch {
    return false;
  }
}
